import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import type { DocumentData, Firestore } from 'firebase/firestore';
import type {
  StakkoFriendship,
  StakkoFriendStatus,
  StakkoFriendView,
} from '../types';
import { publishStakkoNotification } from './notifications';

export interface StakkoFriendIdentity {
  uid: string;
  email?: string | null;
  displayName?: string | null;
  photoURL?: string | null;
}

export function stakkoPairId(a: string, b: string): string {
  return [a, b].sort().join('__');
}

function friendshipRef(db: Firestore, a: string, b: string) {
  return doc(db, 'vibiz_friendships', stakkoPairId(a, b));
}

function toFriendship(id: string, data: DocumentData): StakkoFriendship {
  const users = Array.isArray(data.users) ? (data.users as string[]) : [];
  return {
    id,
    users: [users[0] ?? '', users[1] ?? ''] as [string, string],
    fromUid: typeof data.fromUid === 'string' ? data.fromUid : '',
    toUid: typeof data.toUid === 'string' ? data.toUid : '',
    fromEmail: typeof data.fromEmail === 'string' ? data.fromEmail : null,
    toEmail: typeof data.toEmail === 'string' ? data.toEmail : null,
    fromName: typeof data.fromName === 'string' ? data.fromName : null,
    toName: typeof data.toName === 'string' ? data.toName : null,
    fromPhoto: typeof data.fromPhoto === 'string' ? data.fromPhoto : null,
    toPhoto: typeof data.toPhoto === 'string' ? data.toPhoto : null,
    status: (data.status as StakkoFriendStatus) ?? 'pending',
    createdAt: data.createdAt,
    acceptedAt: data.acceptedAt,
  };
}

export function toStakkoFriendView(friendship: StakkoFriendship, uid: string): StakkoFriendView {
  const otherIsFrom = friendship.fromUid !== uid;
  const otherUid = otherIsFrom ? friendship.fromUid : friendship.toUid;
  const email = (otherIsFrom ? friendship.fromEmail : friendship.toEmail) ?? null;
  const name =
    (otherIsFrom ? friendship.fromName : friendship.toName) ||
    (email ? email.split('@')[0] : 'Student');
  const photoURL = (otherIsFrom ? friendship.fromPhoto : friendship.toPhoto) ?? null;

  return {
    id: friendship.id,
    uid: otherUid,
    name,
    email,
    photoURL,
    status: friendship.status,
    incoming: friendship.status === 'pending' && friendship.toUid === uid,
  };
}

/**
 * Najde uživatele podle e-mailu. Primárně v `vibiz_profiles`, fallback do
 * staré kolekce `users` (tu plní stávající aplikace).
 */
/**
 * Najde uživatele podle UID. Používá se pro staré pozvánky, které mají jen UID.
 */
export async function findStakkoUserByUid(
  db: Firestore,
  uid: string,
): Promise<StakkoFriendIdentity | null> {
  if (!uid) return null;

  const profileSnap = await getDoc(doc(db, 'vibiz_profiles', uid));
  if (profileSnap.exists()) {
    const data = profileSnap.data();
    return {
      uid,
      email: typeof data.emailLower === 'string' ? data.emailLower : null,
      displayName: typeof data.displayName === 'string' ? data.displayName : null,
      photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    };
  }

  const legacySnap = await getDoc(doc(db, 'users', uid));
  if (legacySnap.exists()) {
    const data = legacySnap.data();
    return {
      uid,
      email: typeof data.email === 'string' ? data.email : null,
      displayName:
        typeof data.displayName === 'string'
          ? data.displayName
          : typeof data.email === 'string'
            ? data.email.split('@')[0]
            : null,
      photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    };
  }

  return null;
}

export async function findStakkoUserByEmail(
  db: Firestore,
  email: string,
): Promise<StakkoFriendIdentity | null> {  const normalized = email.trim().toLowerCase();
  if (!normalized.includes('@')) return null;

  const profileSnap = await getDocs(
    query(collection(db, 'vibiz_profiles'), where('emailLower', '==', normalized), limit(1)),
  );
  if (!profileSnap.empty) {
    const item = profileSnap.docs[0];
    const data = item.data();
    return {
      uid: item.id,
      email: normalized,
      displayName: typeof data.displayName === 'string' ? data.displayName : null,
      photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
    };
  }

  for (const candidate of Array.from(new Set([normalized, email.trim()]))) {
    const legacySnap = await getDocs(
      query(collection(db, 'users'), where('email', '==', candidate), limit(1)),
    );
    if (!legacySnap.empty) {
      const item = legacySnap.docs[0];
      const data = item.data();
      return {
        uid: item.id,
        email: candidate,
        displayName:
          typeof data.displayName === 'string'
            ? data.displayName
            : candidate.split('@')[0],
        photoURL: typeof data.photoURL === 'string' ? data.photoURL : null,
      };
    }
  }

  return null;
}

/**
 * Pošle žádost o přátelství. Pokud už existuje opačná pending žádost,
 * rovnou ji přijme (oboustranný klik = přátelé).
 */
export async function sendStakkoFriendRequest(
  db: Firestore,
  from: StakkoFriendIdentity,
  to: StakkoFriendIdentity,
): Promise<StakkoFriendStatus> {
  if (from.uid === to.uid) throw new Error('CANNOT_FRIEND_SELF');

  const ref = friendshipRef(db, from.uid, to.uid);
  const snap = await getDoc(ref);

  if (snap.exists()) {
    const existing = toFriendship(snap.id, snap.data());
    if (existing.status === 'accepted') return 'accepted';
    if (existing.status === 'blocked') return 'blocked';
    if (existing.status === 'pending' && existing.fromUid !== from.uid) {
      await updateDoc(ref, {
        status: 'accepted',
        acceptedAt: serverTimestamp(),
      });
      await publishStakkoNotification(db, {
        toUid: existing.fromUid,
        fromUid: from.uid,
        type: 'friend_accepted',
        title: `${from.displayName || 'Student'} přijal(a) tvou žádost`,
        body: 'Teď si můžete sdílet rozvrhy, mapy a materiály.',
        url: 'https://stakko.cz/ucet',
      });
      return 'accepted';
    }
    if (existing.status === 'pending' && existing.fromUid === from.uid) {
      return 'pending';
    }
    if (existing.status === 'declined' && existing.fromUid !== from.uid) {
      // Nová žádost po předchozím odmítnutí – přepiš dokument, aby pravidla
      // viděla konzistentní fromUid/toUid.
      await deleteDoc(ref);
    }
  }

  await setDoc(
    ref,
    {
      users: [from.uid, to.uid].sort() as [string, string],
      fromUid: from.uid,
      toUid: to.uid,
      fromEmail: from.email ?? null,
      toEmail: to.email ?? null,
      fromName: from.displayName ?? null,
      toName: to.displayName ?? null,
      fromPhoto: from.photoURL ?? null,
      toPhoto: to.photoURL ?? null,
      status: 'pending',
      createdAt: serverTimestamp(),
    },
    { merge: true },
  );

  await publishStakkoNotification(db, {
    toUid: to.uid,
    fromUid: from.uid,
    type: 'friend_request',
    title: `${from.displayName || 'Student'} si tě chce přidat`,
    body: 'Přijmi žádost a propojte si rozvrhy a materiály.',
    url: 'https://stakko.cz/ucet',
    payload: { friendshipId: ref.id },
  });

  return 'pending';
}

export async function acceptStakkoFriendRequest(
  db: Firestore,
  friendshipId: string,
  uid: string,
  by?: StakkoFriendIdentity,
): Promise<void> {
  const ref = doc(db, 'vibiz_friendships', friendshipId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('FRIENDSHIP_NOT_FOUND');

  const friendship = toFriendship(snap.id, snap.data());
  if (friendship.toUid !== uid) throw new Error('NOT_RECIPIENT');

  await updateDoc(ref, {
    status: 'accepted',
    acceptedAt: serverTimestamp(),
  });

  await publishStakkoNotification(db, {
    toUid: friendship.fromUid,
    fromUid: uid,
    type: 'friend_accepted',
    title: `${by?.displayName || 'Student'} přijal(a) tvou žádost`,
    body: 'Teď si můžete sdílet rozvrhy, mapy a materiály.',
    url: 'https://stakko.cz/ucet',
  });
}

export async function declineStakkoFriendRequest(
  db: Firestore,
  friendshipId: string,
  uid: string,
): Promise<void> {
  const ref = doc(db, 'vibiz_friendships', friendshipId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;
  const friendship = toFriendship(snap.id, snap.data());
  if (friendship.toUid !== uid) throw new Error('NOT_RECIPIENT');

  await updateDoc(ref, { status: 'declined' });
}

export async function removeStakkoFriend(
  db: Firestore,
  friendshipId: string,
): Promise<void> {
  await deleteDoc(doc(db, 'vibiz_friendships', friendshipId));
}

export function subscribeStakkoFriendships(
  db: Firestore,
  uid: string,
  onData: (friendships: StakkoFriendship[]) => void,
): () => void {
  const q = query(
    collection(db, 'vibiz_friendships'),
    where('users', 'array-contains', uid),
  );

  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((item) => toFriendship(item.id, item.data()));
      items.sort((a, b) => {
        if (a.status !== b.status) return a.status === 'pending' ? -1 : 1;
        return a.id.localeCompare(b.id);
      });
      onData(items);
    },
    (error) => {
      console.warn('[stakko] přátele se nepodařilo načíst', error);
      onData([]);
    },
  );
}
